import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild, Output, EventEmitter, SimpleChanges, Input, ChangeDetectorRef, ElementRef, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import {
  Ingredient,
  IngredientMacro,
  IngredientReal,
  IngredientType,
  isIngredientMacro,
  isIngredientReal,
  nutrientsForWeightForDisplay,
} from '@cf-shared/data-model/ingredient';
import { CsInputMeasureComponent } from 'components/cs-input-measure/cs-input-measure';
import { DocWatcher } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { FoodDbService, FoodReadFailedError } from 'services/food-db.service';
import { FoodReadFailurePolicy } from 'services/food-read-error';
import { showFoodLoadFailedAlertAsync } from 'services/food-load-failed-alert';
import { IngredientListLoadResult, runIngredientListLoadAsync, toIngredientList } from './ingredient-list-load';
import { sumIngredientListNutrients } from './ingredient-list-totals';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { MeasureUnitConfig } from '@cf-shared/measure-configs';
import { TelemetryService } from 'services/telemetry.service';
import { NavController } from 'providers/nav-controller';
import { LoggingService, isTransientFetchError } from 'services/logging.service';
import { CsCardInterfaceClass } from 'components/cs-card/cs-card';
import { SettingsService } from 'services/settings.service';
import { LoadingController } from 'providers/loading-controller';
import { ComplexFood, Food, Nutrients, NutrientTargets, foodStatusIcons, FoodStatus, isComplexFood, foodTypeIcons, FoodType, isLogMealById, FoodHeader } from '@cf-shared/data-model/food';
import { hasSplitParts } from '@cf-shared/data-model/split-recipe-portion';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { ingredientsToRepeat, splitRecentMeals } from './recent-meals-layout';
import { FormatNutrients, translateMeasureName, isBuiltInMeasureName } from 'util/formatting';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { isRecipeById } from '@cf-shared/data-model/recipe';
import { CsCardListComponent } from 'components/cs-card-list/cs-card-list';
import { AlertController } from 'providers/alert-controller';
import { EditIngredientMode, EditIngredientResult, IEditIngredientPage } from 'pages/edit-ingredient/edit-ingredient-interface';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { FoodsPageResult, IFoodsPage } from 'pages/foods/foods-interface';
import { ToastController } from 'providers/toast-controller';
import { AuthService } from 'services/auth.service';
import { ToastType } from 'components/toast/toast-host';
import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';
import { ListChange, ListUndoOutcome, undoListChange } from './list-change-undo';
import { markerAfterAdding } from 'util/meal-source-marker';
import { buildFoodAddedToastMessage, FOOD_ADDED_TOAST_DURATION_MS, FOOD_ADDED_TOAST_PRESENTATION } from 'util/food-added-toast';
import { IEditFoodPage } from 'pages/edit-food/edit-food-interface';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { CardListDragService, CardListDragConfig } from 'services/card-list-drag.service';
import { DestroyGuard } from 'services/browser-utils';
import { IngredientAddTelemetryFood, IngredientEditReturnContext, ingredientAddsToTrack } from 'components/ingredient-search/ingredient-add-telemetry';
import { defaultIngredientForFood } from 'services/default-ingredient-for-food';
import { foodIdForTelemetry, IngredientRemovalSource, editResultRemovesIngredient } from 'components/ingredient-search/ingredient-remove-telemetry';

/**
 * A change a toast offers to take back (`ListChange`), plus the meal's source marker from just
 * before it, which Undo puts back too (#6188).
 */
interface UndoableListChange extends ListChange {
  markerBefore: string | undefined;
}

/**
 * The per-ingredient display data the food cards need, worked out by reading the food from
 * the database. It is deliberately NOT stored on the ingredient object, because the
 * ingredient is what gets written back to the meal document and none of this belongs there.
 *
 * It is held in a `WeakMap` keyed by the ingredient object rather than in an array indexed
 * in step with `ingredients`. A parallel array made every method that adds, removes or
 * reorders an ingredient responsible for moving the second array the same way, and they did
 * not all do it -- what kept the screen correct was an indirect round trip out to the parent
 * page and back in through `ngOnChanges`, which rebuilt the whole thing. Keying off the
 * ingredient removes the question: there is no index to keep aligned (df-92963447fa12).
 */
interface IngredientDisplayData {
  /** the food's measures, translated for display, used to show "1.8 cups" */
  customIngredientUnits: MeasureUnitConfig[];
  /** nutrients already scaled to this ingredient's weight, for display only (never persisted) */
  cachedNutrients?: Nutrients;
  /** the loaded food document's own status. The ingredient's stored status is often worked out
   * from the id alone, which reads the user's own `food/+` foods as Contributed; the document from
   * the user's database says Private, so the card shows the person icon instead of "Community". */
  foodStatus?: FoodStatus;
  /** for a recipe or logged meal: whether it has any parts to split into. `false` only when the
   * loaded document is known to have none; unset when the food is not complex or could not be
   * read (df-5e2bcf38fbf2). */
  hasSplitParts?: boolean;
}

@Component({
  selector: 'ingredient-list',
  templateUrl: 'ingredient-list.html',
  host: { 'class': 'ingredient-list' },
  providers: [
    { provide: CsCardInterfaceClass, useExisting: IngredientList },
    CardListDragService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class IngredientList extends CsCardInterfaceClass implements AfterViewInit, OnDestroy, OnInit {
  private destroyGuard = new DestroyGuard();
  protected LL = LL;
  @ViewChild('measureInput') measureInput: CsInputMeasureComponent;
  @ViewChild('weightInput') weightInput: CsInputMeasureComponent;
  @ViewChild(CsCardListComponent) list: CsCardListComponent;

  // a parent can bind nothing here -- edit-food binds `recipe?.ingredients` while the recipe
  // is still loading, and edit-meal binds `meal.ingredients`, which a stored meal document
  // can be missing. The `= []` below does NOT survive that: Angular writes the undefined
  // straight over it. Normalise it here so neither this class nor the template can read
  // `.length` off undefined (#4547).
  @Input({ transform: toIngredientList }) ingredients: Ingredient[] = [];

  @Output() updateNutrients: EventEmitter<Nutrients> = new EventEmitter();
  @Output() updateIngredients: EventEmitter<Ingredient[]> = new EventEmitter();
  @Output() updateTotalWeight: EventEmitter<number> = new EventEmitter();
  /** The meal's source marker changed; `undefined` means it was cleared (an Undo, #6188). */
  @Output() updateBasedOnFoodId: EventEmitter<string | undefined> = new EventEmitter();
  /**
   * The meal's source marker (`basedOnFoodId`: the recipe or past meal it was built from), so a
   * change that sets it can be undone back to it (#6188). The recipe editor leaves it unset.
   */
  @Input() basedOnFoodId?: string;
  @Input() isReadOnly: boolean;
  @Input() showHeader: boolean = true;
  @Input() validityMessage: string = undefined;
  @Input() validityMessageImmediate: boolean = false;
  @Output() findFoodHandler: EventEmitter<void> = new EventEmitter();
  @Output() quickMacrosHandler: EventEmitter<void> = new EventEmitter();
  @Input() showHeaderMenu: boolean = false;
  @Input() headerTitleKey: string = 'ingredientSearch.title';
  @Input() headerMenuItems: PopupMenuButton[] = [];
  @Output() headerMenuHandler: EventEmitter<Event> = new EventEmitter();
  protected reorderMode = false;
  @Input() returnCallbackName: string;
  // Passed straight through to the portion screen as `local.targets`, which is where the
  // rule about WHICH target figure belongs here is written down: the meal's own plan
  // targets, never the stored `planningTargets` (#5297).
  @Input() targets: NutrientTargets;
  // Passed straight through to the portion screen, whose macro card needs them to
  // offer its `Day` scope. What they mean and why they travel together is on
  // `EditIngredientPageParams.local` in edit-ingredient-interface.ts.
  @Input() dayDate?: string;
  @Input() currentMealId?: string;
  @Input() showRecentMeals: boolean = true;

  /**
   * Whether the "Add Food" button under the last ingredient is drawn.
   *
   * The meal screen turns it off, because its own add-routes block sits directly above the list
   * and already offers Add Foods along with every other way in (#4969) -- a second button doing
   * one of those things a few centimetres below is noise. The recipe editor keeps it: that screen
   * has no such block, so this button is its only way to add another ingredient.
   */
  @Input() showAddFoodFooter: boolean = true;
  private docWatcher: DocWatcher;
  @Input() currentMealName: string;
  @Input() emptyStateTitleKey: string = 'ingredientSearch.emptyState.title';

  /**
   * Whether the list is drawing the meal screen's recent-meals section: an empty, editable list
   * that shows recent meals, while they are loading or once there is at least one (#5522).
   *
   * Worked out on every read rather than stored, for the reason `routesAsGrid` on the meal page
   * is: the ingredients and the recent meals each change on their own schedule, and a stored
   * answer would have to be told about both.
   */
  protected get showsRecentMealsSection(): boolean {
    if (!this.showsEmptyMealScreenList) {
      return false;
    }
    if (this.recentMealsLoading) {
      return true;
    }
    // decided from the same split the card draws from: it drops damaged entries older versions
    // wrote, so counting the raw list could leave a titled card with nothing in it
    const split = splitRecentMeals(this.recentMeals, this.currentMealName);
    return split.hero !== undefined || split.others.length > 0;
  }

  /**
   * An empty meal with no recent meals to offer draws no header. "Foods" would name a list that
   * is not there, and "Repeat a Recent Meal" a section with nothing in it (#5522).
   */
  protected get hidesHeaderForEmptyMeal(): boolean {
    return this.showsEmptyMealScreenList && !this.showsRecentMealsSection;
  }

  /** the meal screen's list (the only one that shows recent meals) while it is editable and empty */
  private get showsEmptyMealScreenList(): boolean {
    return this.showRecentMeals && !this.isReadOnly && (this.ingredients?.length ?? 0) === 0;
  }

  get headerTitle(): string {
    // while the meal is empty the header names the recent-meals section under it (#5522)
    if (this.showsRecentMealsSection) {
      return LL.ingredientSearch.repeatMeal.sectionTitle();
    }
    // ingredientSearch.title doesn't exist in i18n - use titleMeal as the default
    if (this.headerTitleKey === 'ingredientSearch.title' || this.headerTitleKey === 'ingredientSearch.titleMeal') {
      return LL.ingredientSearch.titleMeal();
    }
    return this.headerTitleKey;
  }

  get emptyStateTitle(): string {
    if (this.emptyStateTitleKey === 'ingredientSearch.emptyState.title') {
      return LL.ingredientSearch.emptyState.title();
    }
    if (this.emptyStateTitleKey === 'ingredientSearch.emptyState.titleRecipe') {
      return LL.ingredientSearch.emptyState.titleRecipe();
    }
    return this.emptyStateTitleKey;
  }
  protected foodStatusIcons = foodStatusIcons;
  protected selectedItems: Ingredient[] = [];
  protected recentMeals: FoodHeader[] = [];
  protected recentMealsLoading: boolean = false;

  showIngredientLoadSpinner: boolean;

  IngredientType = IngredientType;
  EditIngredientMode = EditIngredientMode;

  private heightCache: number = 0;
  private resizeObserver: ResizeObserver | undefined;
  private dragConfig: CardListDragConfig;

  // stable key map for trackBy - assigns unique keys to ingredient objects
  // this prevents Angular from recreating DOM elements when array is reordered
  private ingredientKeyMap = new WeakMap<Ingredient, string>();
  private nextIngredientKey = 0;

  updateLayout(): void {
    // re-measure every card (clears any stale cached height, e.g. after leaving reorder mode or
    // after a tab/scan made the cards lay out while hidden) and reposition, then refresh the
    // host's own height var. Without the per-card re-measure the list reuses stale heights and
    // the cards only snap into place on the next scroll.
    this.list?.remeasureAndReposition();
    this.updateHeight();
  }

  getCurrentCardHeight(): number {
    if (this.heightCache > 0) {
      return this.heightCache;
    }
    if (this.elemRef && this.elemRef.nativeElement) {
      const height = this.elemRef.nativeElement.offsetHeight;
      if (height > 0) {
        this.heightCache = height;
      }
      return height;
    }
    return 0;
  }

  getCardElement(): HTMLElement {
    return this.elemRef.nativeElement;
  }

  private updateHeight(): void {
    if (!this.elemRef || !this.elemRef.nativeElement) {
      return;
    }
    const element = this.elemRef.nativeElement as HTMLElement;
    const newHeight = element.offsetHeight;
    if (newHeight > 0 && newHeight !== this.heightCache) {
      const oldHeight = this.heightCache;
      this.heightCache = newHeight;
      element.style.setProperty('--current-height', `${newHeight}`);
      element.style.setProperty('--current-height-px', `${newHeight}px`);
      if (this.list && oldHeight !== newHeight) {
        requestAnimationFrame(() => {
          this.list.updatePositions();
        });
      }
    }
  }

  /** Everything the food cards need to draw one ingredient that is NOT stored on the
   * ingredient itself. Kept out of the ingredient object so none of it reaches the db. */
  private ingredientDataMap = new WeakMap<Ingredient, IngredientDisplayData>();

  protected FoodStatus = FoodStatus;

  async ngOnChanges(changes: SimpleChanges) {
    let totalWeight: number;
    if (changes.ingredients) {
      void this.loadIngredientsAsync(); // is this correct? we are using ingredients below, perhaps it should be serialized??
    }

    if (changes.totalWeight) {
      totalWeight = changes.totalWeight.currentValue;
    }

    if (totalWeight) {
      this.totalWeight = totalWeight;
      return;
    }
  }

  // Nutrients
  @Input() protected totalWeight = 0;
  protected totalNutrients: Nutrients = {};
  /**
   * Which run of `updateNutrientsAsync` is the current one. Every run takes the next number
   * as it starts, and a run whose number is no longer this one publishes nothing -- see that
   * method for why two of them can be going at once.
   */
  private nutrientRunId = 0;

  @HostBinding('class.animate') animate = this.settings.cardAnimations;

  constructor(
    public loadingCtrl: LoadingController,
    public navCtrl: NavController,
    public userDb: UserDb,
    public foodDb: FoodDbService,
    private telemetry: TelemetryService,
    private cd: ChangeDetectorRef,
    private logging: LoggingService,
    public elemRef: ElementRef,
    public settings: SettingsService,
    private alertController: AlertController,
    
    private popupMenuProvider: PopupMenuProvider,
    public dragService: CardListDragService,
    private toastCtrl: ToastController,
    private auth: AuthService,
  ) {
    super(elemRef);
  }

  async ngOnInit() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.destroyGuard.isDestroyed) return;
        // skip re-render while drag-reorder is active — DOM invalidation breaks the drag
        if (this.dragService.isAnyDragActive()) return;
        this.cd.markForCheck();
      });
    }

    // if showing recent meals in empty state
    if (this.showRecentMeals) {
      void this.loadRecentMealsAsync();
    }
  }

  // Load 3 recent meals for empty state display
  // Prioritizes: 1 meal matching current slot (e.g., "Meal 1"), then 2 more recent meals
  async loadRecentMealsAsync() {
    this.recentMealsLoading = true;
    this.cd.markForCheck();

    try {
      // the open meal never offers itself: the list is loaded once, when the page opens, and a
      // meal entered with food and then emptied would otherwise appear as its own last time (#6188)
      const allRecentMeals = await this.foodDb.searchRecentMealsByName_userAsync('', this.currentMealId);
      if (this.destroyGuard.isDestroyed) return;

      // if we have a current meal name, prioritize the most recent matching meal slot
      if (this.currentMealName && allRecentMeals.length > 0) {
        // find the most recent meal with matching name (e.g., most recent "Meal 1")
        let mostRecentMatch: FoodHeader | undefined;
        let mostRecentTime: string | undefined;

        for (const meal of allRecentMeals) {
          const logMeal = meal.cachedFood as Log_Meal;
          if (logMeal?.name === this.currentMealName) {
            // if this is more recent than our current best match
            if (!mostRecentTime || logMeal.time_utc > mostRecentTime) {
              mostRecentMatch = meal;
              mostRecentTime = logMeal.time_utc;
            }
          }
        }

        if (mostRecentMatch) {
          // start with the matching meal, then add 2 more (excluding the match)
          const otherMeals = allRecentMeals.filter(m => m !== mostRecentMatch);
          this.recentMeals = [mostRecentMatch, ...otherMeals.slice(0, 2)];
        } else {
          // no matching meal found, just take first 3
          this.recentMeals = allRecentMeals.slice(0, 3);
        }
      } else {
        this.recentMeals = allRecentMeals.slice(0, 3);
      }
    } catch (e) {
      // a transient network drop (offline, or the iOS "network connection was lost") just means
      // the recent-meals strip can't populate this time — it self-heals on the next open, so it is
      // not worth a Sentry report (Sentry triage v1.25.0 Group 11, issue 3XR). Only a genuinely
      // unexpected failure surfaces.
      if (isTransientFetchError(e)) {
        console.warn('Recent meals load skipped (transient network error):', (e as any)?.message ?? e);
      } else {
        this.logging.trackSilentProblem('Failed to load recent meals', e);
      }
      this.recentMeals = [];
    }

    if (this.destroyGuard.isDestroyed) return;
    this.recentMealsLoading = false;
    this.cd.markForCheck();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateHeight();
      });
      this.resizeObserver.observe(this.elemRef.nativeElement);
    }
    setTimeout(() => {
      this.updateHeight();
      if (this.list) {
        this.list.updatePositions();
      }
    }, 0);

    // initialize drag config for the drag service
    this.dragConfig = {
      cardSelector: 'cs-food-card.ingredient-item',
      topBoundSelector: 'cs-card-list-row.ingredient-list-header',
      bottomBoundSelector: 'cs-card-list-row.ingredient-list-footer, cs-card-list-row.validation-row',
      hostElement: this.elemRef.nativeElement,
      listComponent: this.list,
      changeDetector: this.cd,
      onReorder: (fromIndex, toIndex) => {
        // the display data travels with the ingredient object, so moving the ingredient is
        // the whole job -- there is no second list to move the same way
        const [removedIngredient] = this.ingredients.splice(fromIndex, 1);
        this.ingredients.splice(toIndex, 0, removedIngredient);
      },
      onDragStart: () => {
        // enter reorder mode when long-press drag activates (after 500ms hold)
        if (!this.reorderMode) {
          this.reorderMode = true;
          this.cd.markForCheck();
        }
      },
      onDragEnd: () => {
        this.updateIngredients.emit(this.ingredients);
        // the reorder saves the meal, so it needs an event of its own: without one the meal
        // document gains a revision that nothing in the data explains (#4536)
        this.telemetry.trackEvent('action_ingredient_reorder', { count: this.ingredients.length });
      }
    };
  }

  ngOnDestroy(): void {
    this.destroyGuard.destroy();
    this.docWatcher?.destroy();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // clean up drag service
    this.dragService.destroy();
  }

  isIngredientSavedRecipe(ingredient: Ingredient): boolean {
    return isIngredientReal(ingredient) && isRecipeById(ingredient.foodId);
  }

  isIngredientReal = isIngredientReal;

  // desktop: always show handles; mobile: only in reorder mode
  get showDragHandles(): boolean {
    if (this.isReadOnly) return false;
    return !hasClientSmallScreen() || this.reorderMode;
  }

  // show header ellipsis on mobile for reorder (even if parent didn't set showHeaderMenu)
  get showReorderMenuButton(): boolean {
    return hasClientSmallScreen() && !this.isReadOnly && this.ingredients.length > 1 && !this.reorderMode;
  }

  enterReorderMode() {
    this.reorderMode = true;
    this.cd.markForCheck();
  }

  exitReorderMode() {
    this.reorderMode = false;
    this.cd.markForCheck();
    // Leaving reorder mode shrinks the cards back to their normal height and removes the Done
    // header row. Wait two frames so Angular has applied that DOM change, then re-measure every
    // card and reposition — otherwise the list keeps the taller reorder-mode heights and the
    // cards sit with stale gaps until the next scroll.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (this.destroyGuard.isDestroyed) return;
      this.updateLayout();
    }));
  }

  /** The display data the food cards need for one ingredient, or undefined while it is still
   * being loaded. The template uses this to decide whether the card can be drawn yet. */
  protected ingredientDataFor(ingredient: Ingredient): IngredientDisplayData | undefined {
    return this.ingredientDataMap.get(ingredient);
  }

  /** Minimal display data: a visible card with no measures/nutrients. Used both for a
   * genuinely missing food (so the user can see and remove it) and for a transient read
   * failure (so the row stays visible and upgrades on the next reload). */
  private blankIngredientData(): IngredientDisplayData {
    return { customIngredientUnits: [] };
  }

  async loadOneIngredientAsync(ingredientOrIndex: number | Ingredient) {
    const ingredient = typeof ingredientOrIndex == 'number'
      ? this.ingredients[ingredientOrIndex]
      : ingredientOrIndex;

    if (isIngredientReal(ingredient)) {
      let food: Food;
      try {
        // ask for Throw so a transient read failure (network blip, iOS
        // "Load failed", or the local DB mid-recovery) is NOT mistaken for a missing food.
        food = await this.foodDb.getFoodByIdAsync(ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw }); // TODO: can we not always cache here!!!!
      } catch (err) {
        if (err instanceof FoodReadFailedError) {
          // could not read the food this time (transient network / DB mid-recovery; the real
          // error was already surfaced inside getFoodByIdAsync). Do NOT report it as missing.
          // Keep any last-good card; if there is none yet, show a minimal placeholder so the
          // row stays visible (not hidden) during the outage. It upgrades to full data on the
          // next list reload (re-opening / navigating to the meal, or editing it).
          if (!this.ingredientDataMap.has(ingredient)) {
            this.ingredientDataMap.set(ingredient, this.blankIngredientData());
          }
          return;
        }
        throw err;
      }
      if (!food) {
        // genuine not-found (404): the food really is gone. Use a stable problem name and
        // put the id in context so Sentry groups all of these as one issue instead of one
        // issue per food id.
        this.logging.trackSilentProblem('Nonexistent food added to ingredients', { foodId: ingredient.foodId, detail: LL.ingredientSearch.errors.foodDoesNotExist() });
        // still show the ingredient card so the user can see and remove it
        this.ingredientDataMap.set(ingredient, this.blankIngredientData());
        return;
      }
      food.measures = food.measures || [];

      let customIngredientUnits = food && food.measures && food.measures.map(m => ({
        // id stays the canonical English measure name -- it is the stored identity, compared
        // against ingredient.custom_unit elsewhere. Only the display fields get translated.
        id: m.name,
        description: translateMeasureName(m.name),
        unit1: translateMeasureName(m.name),
        // a translated built-in name can be multiple words (e.g. es "cuarto de galón" for
        // `quart`) -- doNotShortenUnit1 stops cs-input-measure/virtual-keyboard from running
        // it through shortUnitName's word-truncation, the same rule formatServingSize follows
        doNotShortenUnit1: isBuiltInMeasureName(m.name),
        value1: m.weight_g / m.amount,
        fractionDenominatorMax: 8,
      }));

      this.ingredientDataMap.set(ingredient, {
        customIngredientUnits: customIngredientUnits,
        foodStatus: food.status,
        hasSplitParts: isComplexFood(food) ? hasSplitParts(food) : undefined,
      });
    } else if (isIngredientMacro(ingredient)) {
      if (ingredient.status === undefined) {
        ingredient.status = FoodStatus.Custom;
      }
      // a hand-entered food has no food document and so no measures to offer
      this.ingredientDataMap.set(ingredient, this.blankIngredientData());
    }
  }

  /** Load the display data for every ingredient, and say how that ended.
   *
   * A caller that goes on to PERSIST something worked out from the meal's totals must
   * check the result: on anything but `Completed` the totals were never recalculated, so
   * they are stale. A caller that only redraws the screen may ignore it -- the failure has
   * already been reported and the user has already been told (#4547).
   *
   * TODO: This could be merged with updateNutrientsAsync(), but take care about not
   * modifying official foods nutrients accidentally during caching.
   */
  async loadIngredientsAsync(): Promise<IngredientListLoadResult> {
    // the loop, the rule that the spinner is cleared whatever happens, and the choice
    // between the two failure reports below all live in ingredient-list-load.ts so they can
    // be unit-tested without Angular (#4547)
    return runIngredientListLoadAsync({
      ingredientCount: () => this.ingredients.length,
      loadOneAsync: i => this.loadOneIngredientAsync(i),
      updateNutrientsAsync: () => this.updateNutrientsAsync(),
      isDestroyed: () => this.destroyGuard.isDestroyed,
      setSpinnerVisible: visible => { this.showIngredientLoadSpinner = visible; },
      markForCheck: () => this.cd.markForCheck(),
      // three of this class's own calls start the load without awaiting it, so a throw
      // there has no caller to catch it -- report it here or it becomes an unhandled
      // rejection and the user is left with a half-loaded meal and no idea why.
      reportLoadFailureToUser: err => this.logging.trackAbnormalException(err, LL.errors.userFacing.ingredientListLoadFailed()),
      // nobody is on the screen any more, so this reports without showing anything. It
      // cannot be trackAbnormalException with no message: that is NOT silent, it falls back
      // to the generic "Notice anything unusual?" toast, which would land on whatever screen
      // the user is looking at now. It is a problem rather than an info because nothing
      // repaired the load.
      reportLoadFailureSilently: err => this.logging.trackSilentProblem('Ingredient list load failed after the view was destroyed', { error: describeErrorForLog(err) }),
    });
  }

  /**
   * Work out the meal's nutrient totals and publish them.
   *
   * Two runs of this CAN overlap -- several callers start one without awaiting it, and
   * `loadIngredientsAsync` ends with one of its own, so a caller that starts a load and then
   * asks for totals has two in flight at once. That used to add a food into the totals twice
   * (deferred finding df-ad69b55547e9): the old code emptied `this.totalNutrients` as its
   * first statement and every per-food promise added into whatever object that field held at
   * the moment it resolved, so a promise from the earlier run landed in the LATER run's
   * totals, on top of that run's own figure for the same food. The meal then read higher than
   * its foods add up to -- on the meal screen only until the list reloaded, but on the recipe
   * screen `edit-food` keeps the emitted object as `food.nutrients` and saves it as-is, so a
   * recipe could be stored with the doubled figure for good.
   *
   * So this run first reads every food it needs, and only then adds them up -- in
   * `sumIngredientListNutrients`, which builds a fresh object each time and is where the
   * arithmetic's rules live and are tested (df-2310dc2e8f12). A run that has been superseded
   * adds up and publishes nothing, and reports nothing: the newest run owns the answer.
   * Superseded means a newer run is already working on the same list, so the totals are not
   * left stale -- they arrive when that run finishes.
   */
  public async updateNutrientsAsync() {
    const runId = ++this.nutrientRunId;
    // the list as it is NOW -- a newer run takes over if it changes while the foods are read
    const ingredients = [...this.ingredients];

    // read every food the list points at, once each. A food that is deleted or never synced
    // reads as null and contributes nothing
    const foodsById = new Map<string, Food | null>();
    const reads: Promise<void>[] = [];
    for (const ingredient of ingredients) {
      if (!isIngredientReal(ingredient) || foodsById.has(ingredient.foodId)) continue;
      foodsById.set(ingredient.foodId, null);
      reads.push(
        this.foodDb.getFoodByIdAsync(ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }).then(food => {
          foodsById.set(ingredient.foodId, food ?? null);
        }),
      );
    }

    // wait until all the above is finished
    await Promise.all(reads);
    if (this.destroyGuard.isDestroyed) return;
    // a newer run started while this one was reading its foods, so it -- not this one -- owns
    // what the screen and the host page are told. Publishing here would overwrite a fresher
    // answer with an older one, and that includes the per-food display figures and every report.
    if (runId !== this.nutrientRunId) return;

    const totals = sumIngredientListNutrients(ingredients, foodsById, this.logging, this.currentMealId);

    // store each food's scaled figures beside its row (not on the ingredient object, to avoid
    // db pollution) -- local only, never persisted
    for (const [ingredient, displayNutrients] of totals.displayNutrients) {
      const ingredientData = this.ingredientDataMap.get(ingredient);
      if (ingredientData) {
        ingredientData.cachedNutrients = displayNutrients;
      }
    }

    this.totalWeight = totals.totalWeight_g;
    this.totalNutrients = totals.nutrients;
    this.updateTotalWeight.emit(this.totalWeight);
    this.updateNutrients.emit(this.totalNutrients);
    this.cd.markForCheck();
  }

  getFoodTypeIconForIngredient(ingredient: Ingredient) {
    if (isIngredientReal(ingredient)) {
      // isRecipeById now includes legacy meal IDs - all show Recipe icon
      if (isRecipeById(ingredient.foodId)) {
        return foodTypeIcons[FoodType.Recipe].name;
      }
      return foodTypeIcons[FoodType.Food].name;
    }

    return foodTypeIcons[FoodType.Recipe].name;
  }

  /**
   * @param addedFood the food this screen was opened for, when opening it should be recorded
   *   as an ingredient being added once the user confirms. Only the food-list pick passes it;
   *   see `ingredient-add-telemetry.ts` for why it rides in the return context.
   * @param sourceLogMeal the past meal being confirmed. Passing it also marks the return context
   *   as a past-meal confirmation, which records an add per food once the user confirms (#6237).
   */
  async openEditIngredientModal(ingredientIndex: number, mode: EditIngredientMode, pendingIngredient?: Ingredient, sourceLogMeal?: Log_Meal, addedFood?: IngredientAddTelemetryFood) {
    if (this.isReadOnly || !this.returnCallbackName) {
      return;
    }

    const returnContext: IngredientEditReturnContext = {
      index: ingredientIndex,
      addedFood,
      ...(sourceLogMeal ? { reusedPastMeal: true } : {}),
    };
    await this.navCtrl.pushAsync(IEditIngredientPage, {
      ingredientIndex,
      mode,
      // the EARLY timing, not the ordinary one: the answer is applied and confirmed before the
      // portion screen's closing transition rather than after it, so the "food added" strip
      // appears on the press instead of half a second later. `handleEditResult` navigates
      // nowhere, which is what makes it eligible -- see `returnCallbackImmediate` on CsPage.
      returnCallbackImmediate: this.returnCallbackName,
      local: {
        ingredients: this.ingredients,
        targets: this.targets,
        ...this.dayScopeParams(),
        pendingIngredient: pendingIngredient,
        sourceLogMeal: sourceLogMeal,
        returnContext,
      },
    });
  }

  /**
   * The three values the portion screen's `Day` scope needs, or nothing at all.
   *
   * Built together so the "all three or none" contract on
   * `EditIngredientPageParams.local` is true rather than merely intended. The name
   * comes from `currentMealName`, an input this component already has for its own
   * header, and a host can set that without setting the other two -- `edit-food`
   * does, passing a recipe's name. Sending it alone would leave a meal name beside
   * no day and no meal, which nothing reads today but which the contract forbids.
   */
  private dayScopeParams(): { dayDate?: string, mealId?: string, mealName?: string } {
    if (!this.dayDate || !this.currentMealId) return {};
    return {
      dayDate: this.dayDate,
      mealId: this.currentMealId,
      mealName: this.currentMealName,
    };
  }

  /**
   * The portion screen's answer.
   *
   * Runs EARLY -- before the portion screen's closing transition, while it is still on screen
   * (`returnCallbackImmediate`, see CsPage). That is deliberate: this is what raises the "food
   * added" confirmation, and raised after the transition it arrived about half a second late.
   * Applying the food and offering to undo it must also happen together, or Undo pressed in
   * that half second would find nothing to take back.
   *
   * What it does is DATA: put the food in the list, and raise the toast. It deliberately does
   * not try to make the meal page repaint — that page is covered by two others at this moment
   * and repaints itself on the way back in (`ionViewCanEnter` in `edit-meal.ts`). This
   * component's own `markForCheck` is a different thing and still works: it is guarded by the
   * component's `DestroyGuard`, not by the page's `viewDestroyed`.
   *
   * And NOTHING HERE MAY NAVIGATE. The pop is in flight while this runs, and on a
   * browser-driven pop it runs inside the popstate handler, where a pushed page loses its
   * history entry. Work that needs to navigate belongs in an ordinary `returnCallback`, the
   * way `handleFoodPageResult` does it.
   */
  handleEditResult(context: IngredientEditReturnContext, result: EditIngredientResult) {
    if (!result) return;

    // set by the branch below that starts a full list reload, which recalculates the totals as
    // its last step. The tail of this method must not then start a SECOND recalculation of the
    // same list -- that is what df-ad69b55547e9 was about. The tail cannot simply be removed:
    // the save and delete branches have nothing else that recalculates for them.
    let loadIsRecalculatingTotals = false;

    // if context is missing (e.g. sessionStorage was cleared externally), Sentry 3QW
    if (!context) {
      this.logging.trackSilentProblem('IngredientEditContextLost', { action: result.action });
      return;
    }

    // the food is counted as added HERE, where the user confirmed it — not where the portion
    // screen was opened. See ingredient-add-telemetry.ts for what opening it used to cost, and
    // why a confirmed past meal counts once per food.
    for (const added of ingredientAddsToTrack(context, result)) {
      this.telemetry.trackEvent('action_ingredient_add', {
        status: added.status,
        foodId: added.foodId,
      });
    }

    if (result.action === 'save' && result.ingredient) {
      // if index is -1, this is a new ingredient - add to list
      if (context.index === -1) {
        this.ingredients.push(result.ingredient);
        this.totalWeight += result.ingredient.weight_g;
        this.updateTotalWeight.emit(this.totalWeight);
        void this.loadOneIngredientAsync(result.ingredient);
        this.showFoodAddedToast(result.ingredient, result.loggedAmountDescription);
      } else {
        // The portion screen hands back a NEW ingredient object for the SAME food -- it edits
        // the amount and the measure, never which food this is. Hand the outgoing object's
        // display data to the incoming one, or the new object has none and its card blinks
        // out until something reloads the list, which nothing on this path does.
        const outgoingData = this.ingredientDataMap.get(this.ingredients[context.index]);
        if (outgoingData) {
          this.ingredientDataMap.set(result.ingredient, outgoingData);
        }
        this.ingredients[context.index] = result.ingredient;
      }
    } else if (result.action === 'delete') {
      // `editResultRemovesIngredient` is the #4536 guard, not tidiness, and the `-1` it tests
      // for is the same one the save branch above tests for. The portion screen offers its
      // "Remove" button while the user is still confirming a food that has NOT gone into the
      // meal yet, and that route opened the screen with an index of -1. `splice(-1, 1)` counts
      // from the END, so without this test it deleted the LAST food already in the meal — a
      // food the user logged earlier, gone from the saved meal, while the confirmation they
      // answered named the food they had just picked.
      //
      // Doing nothing IS the right answer for that case: the pending food was never added,
      // so not adding it is exactly what "remove this one" means there. That is also exactly
      // when no removal should be counted, which is why one test answers both questions.
      //
      // The list is settled by this method's tail, together with its other branches.
      if (editResultRemovesIngredient(context, result)) {
        this.takeIngredientOutAt(context.index, 'portion-screen-delete');
      }
    } else if (result.action === 'split' && result.splitIngredients?.length) {
      // the length test is the #4427 guard, not tidiness: an empty list would replace the
      // entry the user logged with nothing at all, deleting their food. an empty array is
      // truthy, so testing `result.splitIngredients` alone does not catch it.
      //
      // skipping the branch also skips the tunedExistingIngredients case below, which would
      // throw away a finished auto-tune. that cannot happen: handleSplitAsync in
      // edit-ingredient.ts is the only thing that ever sets that field, and it returns early
      // on an empty split, so an empty list never arrives with tuned ingredients attached.
      // remembered for the Undo the toast below offers (#6188): the whole list, because Split &
      // Auto-Tune re-weighs rows the meal already held, and the marker, because the split may set it
      const before = deepClone(this.ingredients);
      const markerBefore = this.basedOnFoodId;
      const replaced = context.index === -1 ? undefined : this.ingredients[context.index];
      // if tuned existing ingredients returned, replace all ingredients with tuned versions + split ingredients
      if (result.tunedExistingIngredients) {
        // The tuned rows are every row the meal keeps. The row that WAS split is not among
        // them -- the service leaves it out, because it is about to become its own parts
        // (df-69b7099c360e) -- so the parts go in at the position it occupied, which is where
        // a plain split puts them too. With no position to go back to (the recent-meal
        // confirmation, index -1) they go at the end, as before.
        const tunedRows = [...result.tunedExistingIngredients];
        const insertAt = context.index === -1 ? tunedRows.length : context.index;
        tunedRows.splice(insertAt, 0, ...result.splitIngredients);
        this.ingredients = tunedRows;
      } else if (context.index === -1) {
        // new item (recent meal confirmation) - prepend split ingredients
        this.ingredients.splice(0, 0, ...result.splitIngredients);
      } else {
        this.ingredients.splice(context.index, 1, ...result.splitIngredients);
      }
      void this.loadIngredientsAsync();
      loadIsRecalculatingTotals = true;
      // the meal is "based on" the recipe or past meal only when it held nothing else before:
      // for a row split in place, that row was the only one (#6188)
      const mealWasEmpty = replaced === undefined ? before.length === 0 : before.length === 1;
      this.setSourceMarker(markerAfterAdding(mealWasEmpty, markerBefore, result.basedOnFoodId));
      // every split says what it did and offers to take it back (#5808 for a past meal, #6188
      // for a recipe): a newly picked past meal or recipe as foods added to the meal, a row
      // already in the meal as split into its parts
      const change = this.describeListChange(before, markerBefore, result.splitIngredients, replaced);
      if (replaced === undefined) {
        this.showFoodsAddedToast(change);
      } else {
        this.showSplitToast(change);
      }
    }

    this.updateIngredients.emit(this.ingredients);
    if (!loadIsRecalculatingTotals) {
      void this.updateNutrientsAsync();
    }
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
  }

  /**
   * Say that the food was added, and offer to take it back (#4967).
   *
   * The undo is deliberately an IN-MEMORY removal from this list, not a write to the stored
   * meal. This list is the owner of the meal's working copy: the host page saves it when the
   * list says it changed. Reaching around that to edit the document directly would fight the
   * host's own writer, and would have nothing to edit at all for a meal the user has not
   * saved yet. (The portion screen's OTHER route, where it writes the day's log itself, has
   * its own undo for that reason — the two share the wording, not the action.)
   *
   * Nothing here is a hard failure: a confirmation that cannot be shown must never stop a
   * food being logged.
   */
  private showFoodAddedToast(ingredient: Ingredient, amountDescription: string) {
    // no worded amount means an older portion screen, or a path that does not set one. The
    // toast reads poorly with a blank in the middle of it, so skip it rather than show that.
    if (!amountDescription) return;

    this.toastCtrl.showToast({
      message: buildFoodAddedToastMessage({
        foodName: ingredient.name,
        amountDescription,
        // Blank whenever the host did not name where the food is going — a recipe the user has
        // not titled yet, chiefly. `buildFoodAddedToastMessage` drops the "added to X" half in
        // that case rather than rendering "added to " with nothing after it.
        mealName: this.currentMealName || '',
      }),
      actionButtonText: LL.common.buttons.undo(),
      onAction: () => this.undoAddedIngredient(ingredient),
      ...FOOD_ADDED_TOAST_PRESENTATION,
    });
  }

  /**
   * Where in the list a single food that was just added now sits, or -1 if it is no longer there.
   *
   * Never by remembered POSITION: between adding the food and pressing Undo the user can
   * reorder the list, delete something else, or add more, and a stale index would take out
   * the wrong food.
   *
   * Identity first, then value. Identity is the exact answer, but it does not always survive:
   * adding a food saves the meal, and the host page re-reads the saved meal and hands this
   * component a FRESH array of fresh objects, so the object we were given is often no longer
   * the one in the list. So when identity misses, match by value, searching from the end: a
   * single food is appended, and searching backwards takes the one just added rather than an
   * identical portion of the same food added earlier.
   *
   * Several foods put in at once are found by `undoListChange` instead, by the same rule.
   */
  private findAddedIngredientIndex(ingredient: Ingredient): number {
    const byIdentity = this.ingredients.indexOf(ingredient);
    if (byIdentity >= 0) return byIdentity;

    for (let i = this.ingredients.length - 1; i >= 0; i--) {
      if (deepCompareEQ(this.ingredients[i], ingredient)) return i;
    }
    return -1;
  }

  /**
   * Take one food out of the meal: the bookkeeping every removal route shares, written once.
   *
   * A food leaves a meal by several routes — the portion screen's Remove, the card menu's
   * Remove Food, and the single-food Undo — and each of them used to repeat these same steps
   * by hand, so a change to what "a food leaves a meal" means had to be made in each one
   * and each was free to forget a piece (df-1fef8d67e5fe).
   *
   * What it owns: recording the removal, taking the entry off the list, and keeping the
   * running weight right. The event is recorded BEFORE the entry goes, so it can still read
   * which food is leaving.
   *
   * What it deliberately does NOT own: telling the host page, recalculating the meal's
   * totals, and asking for a repaint. WHEN to do those genuinely differs per route, and
   * doing them here would be wrong for the callers. `handleEditResult` settles once at the end
   * of the method for all of its branches together, and starting a second recalculation there
   * is the bug df-ad69b55547e9 was about. The card menu waits for its recalculation before the
   * menu closes.
   *
   * The caller is responsible for `index` naming a real entry. Nothing removable at that
   * index means nothing is removed and nothing is recorded.
   */
  private takeIngredientOutAt(index: number, source: IngredientRemovalSource): void {
    const ingredient = this.ingredients[index];
    if (!ingredient) return;

    this.telemetry.trackEvent('action_ingredient_remove', {
      foodId: foodIdForTelemetry(ingredient),
      source,
    });
    this.ingredients.splice(index, 1);
    // `updateNutrientsAsync` rebuilds this total from scratch and is what finally settles it.
    // Adjusting it here as well is not bookkeeping the recount depends on -- it is so the
    // figure is right at once rather than for the moment the recount takes to come back.
    //
    // So subtract only what the recount would have counted: it leaves out a typed-in
    // ("macro") row, which carries nutrients rather than a real weight of food, and a row
    // whose weight is missing or unreadable. Subtracting either of those would make the
    // figure disagree with the recount that follows it.
    if (!isIngredientMacro(ingredient) && ingredient.weight_g !== undefined && !isNaN(ingredient.weight_g)) {
      this.totalWeight -= ingredient.weight_g;
    }
  }

  /**
   * Take back the food the toast just announced.
   *
   * Where the food is found is `findAddedIngredientIndex`'s job, above, and its doc comment
   * carries the reasoning. If it finds nothing, say so instead of doing nothing quietly: the
   * user pressed Undo and is owed either the removal or an explanation.
   */
  private undoAddedIngredient(ingredient: Ingredient) {
    if (this.destroyGuard.isDestroyed) return;

    const index = this.findAddedIngredientIndex(ingredient);
    if (index < 0) {
      this.logging.trackSilentProblem('LoggedFoodUndoIngredientGone', { name: ingredient.name });
      this.toastCtrl.showToast({
        message: LL.editIngredient.undoFailedToast(),
        type: ToastType.WarningLight,
        duration: FOOD_ADDED_TOAST_DURATION_MS,
      });
      return;
    }

    // the removal itself, and the `action_ingredient_remove` event that goes with it, are
    // `takeIngredientOutAt`'s job. The `source` tells a take-back apart from a deliberate
    // delete, so neither question needs the other event to answer it (#4536).
    this.takeIngredientOutAt(index, 'added-undo');
    this.updateTotalWeight.emit(this.totalWeight);
    this.updateIngredients.emit(this.ingredients);
    void this.updateNutrientsAsync();
    this.cd.markForCheck();

    // Counted only when the food really came back out — the give-up path above reports through
    // `trackSilentProblem` instead, so a press that could not be carried out is never counted
    // as an undo. Same event as the portion screen's own undo: the two routes undo different
    // things, but the user did one thing and it should read as one number.
    this.telemetry.trackEvent('food_added_undo', {});
  }

  isSelected(ingredient: Ingredient) {
    return this.selectedItems.includes(ingredient);
  }

  changeItemSelection(ingredient: Ingredient) {
    const selected = this.selectedItems.includes(ingredient);
    if (selected) {
      // Remove selection
      this.selectedItems = this.selectedItems.filter(e => e !== ingredient);
    } else {
      // Add selection
      this.selectedItems.push(ingredient);
    }
  }

  /**
   * Take one food out of the meal on the user's behalf, from the card menu's "Remove Food".
   *
   * `takeIngredientOutAt` does the removal and records it; this method settles afterwards --
   * it waits for the recalculation so the menu does not close over a meal still showing the
   * food's calories.
   *
   * A food that is no longer in the list is not an error here: the menu can still be open
   * over an entry the list has already replaced underneath it, and the right answer is to do
   * nothing rather than take out whatever now sits in that position.
   */
  async removeIngredient(ingredient: Ingredient) {
    const index = this.ingredients.indexOf(ingredient);
    if (index < 0) return;

    this.takeIngredientOutAt(index, 'card-menu');
    // Emit all changes to parent view
    await this.updateNutrientsAsync();
  }

  /** Tell the user a food couldn't be read because of a transient connection / DB-recovery
   * issue (NOT because it is missing). Shown for explicit add actions so the user knows to
   * retry, instead of falsely reporting the food as nonexistent. The wording is shared with
   * every other flow that catches this error — see services/food-load-failed-alert.
   *
   * say nothing if the list is gone or the app is signing out: a read that fails because the
   * user is logging out is expected teardown, not a connection problem, and getFoodByIdAsync
   * throws for it before its own sign-out check (design-docs/warm-resume.md, "Sign-out race
   * convention"; df-3dc351d800bb). */
  private async showFoodLoadFailedAlert() {
    if (this.destroyGuard.isDestroyed || this.auth.isLikelySignOutRace()) return;

    await showFoodLoadFailedAlertAsync(this.alertController);
  }

  private generateIngredientFromFood(food: Food) {
    return defaultIngredientForFood(food, food.status || this.foodDb.getFoodStatus(food._id));
  }

  async addIngredientToListAsync(foodId: string) {
    const activePage = this.navCtrl.getActive();
    let food: Food;
    try {
      food = await this.foodDb.getFoodByIdAsync(foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw }); // (TODO: can we not always cache here?!)
    } catch (err) {
      if (this.destroyGuard.isDestroyed) return;
      if (err instanceof FoodReadFailedError) {
        // transient read failure (network blip / DB mid-recovery): the food is not missing,
        // we just couldn't read it. Tell the user it's a connection issue so they can retry,
        // instead of falsely reporting the food as nonexistent.
        await this.showFoodLoadFailedAlert();
        return;
      }
      throw err;
    }
    if (this.destroyGuard.isDestroyed) return;
    if (!food) {
      // this can happen rarely due to database errors or routing problems when refreshing etc
      this.logging.trackAbnormalError(`Nonexistent food added to ingredients: ${foodId}`, LL.errors.userFacing.foodNotExist());
      return;
    }
    await this.foodDb.markFoodAsUsedAsync(food);
    if (this.destroyGuard.isDestroyed) return;
    // if user navigated during async food lookup, abort — pushing EditIngredientPage
    // would land on the wrong parent page (Sentry 3TJ)
    if (this.navCtrl.getActive() !== activePage) return;
    let ingredientToAdd = this.generateIngredientFromFood(food);

    // don't add to list yet - pass as pending ingredient
    // ingredient will be added in handleEditResult when user confirms — and that is also where
    // the "ingredient added" event is recorded, because awaiting this call only tells us the
    // portion screen opened, not that the user kept the food
    await this.openEditIngredientModal(-1, EditIngredientMode.New, ingredientToAdd, undefined, {
      foodId: food._id,
      status: food.status,
    });
  }


  handleFindFood() {
    void this.addFoodAsync();
    this.findFoodHandler.emit();
  }


  async addCustomFoodAsync() {
    let newIngredient: IngredientMacro = {
      type: IngredientType.Custom,
      name: LL.ingredientSearch.customFood(),
      status: FoodStatus.Custom,
      nutrients: {},
      weight_g: 100,
    };

    // don't add to list yet - pass as pending ingredient
    // ingredient will be added in handleEditResult when user confirms
    await this.openEditIngredientModal(-1, EditIngredientMode.New, newIngredient);
  }

  // show confirmation modal for a log meal before adding its ingredients
  private async showLogMealConfirmationAsync(logMealId: string) {
    const logMeal = await this.userDb.getOneLogEntryAsync<Log_Meal>(logMealId, this.docWatcher);
    if (this.destroyGuard.isDestroyed) return;
    if (!logMeal?.ingredients?.length) return;
    const previewIngredient = this.createPlaceholderFromLogMeal(logMeal);
    await this.openEditIngredientModal(-1, EditIngredientMode.New, previewIngredient, logMeal);
  }

  async addIngredientsFromLogMealAsync(logMealId: string) {
    try {
      await this.showLogMealConfirmationAsync(logMealId);
    } catch (e) {
      this.logging.trackAbnormalException(e);
    }
  }

  async addFoodAsync() {
    void this.navCtrl.pushAsync(IFoodsPage, {
      mode: 'select',
      returnCallback: 'handleFoodPageResult',
      // The food list stays open while the portion screen is on top of it (#4967), so
      // logging a food comes back to the list and the next one is a tap away. Without this
      // the list closes the moment a food is tapped, and Log could only ever land back here
      // on the meal.
      keepOpenAfterSelect: true,
      local: {
        returnContext: { source: 'addFood' },
      },
    });
  }

  // Start drag on long press (mobile only - desktop uses drag handle)
  onDragStart(event: PointerEvent, _ingredient: Ingredient, index: number) {
    if (this.isReadOnly) return;
    this.dragService.startLongPress(event, index, this.dragConfig);
  }

  // Immediate drag start from drag handle (desktop only, no long press delay)
  onDragHandleStart(event: PointerEvent, _ingredient: Ingredient, index: number) {
    if (this.isReadOnly) return;
    this.dragService.startDragHandle(event, index, this.dragConfig);
  }

  // Handle click on ingredient card (opens edit modal)
  onIngredientClick(_event: Event, ingredientIndex: number) {
    // ignore clicks that happen right after a drag operation
    if (this.dragService.didDragJustEnd()) {
      return;
    }
    // don't open edit modal if drag is in progress
    if (this.dragService.isAnyDragActive()) {
      return;
    }
    void this.openEditIngredientModal(ingredientIndex, EditIngredientMode.Edit);
  }

  // Map an ingredient to FoodHeader format for food-card component
  mapIngredientToFoodHeader(ingredient: Ingredient): FoodHeader {
    return {
      _id: isIngredientReal(ingredient) ? ingredient.foodId : undefined,
      name: ingredient.name,
      // prefer the loaded food's status, which knows the user's own foods; see IngredientDisplayData
      status: this.ingredientDataMap.get(ingredient)?.foodStatus ?? ingredient.status,
      recipe: isIngredientReal(ingredient) && isRecipeById(ingredient.foodId),
      displayNutrients: this.getIngredientDisplayNutrients(ingredient),
    };
  }

  // Get display nutrients for an ingredient
  getIngredientDisplayNutrients(ingredient: Ingredient): UiDisplayNutrient[] {
    const ingredientData = this.ingredientDataMap.get(ingredient);
    if (!ingredientData) {
      return [];
    }

    // for custom/macro ingredients, scale their per-100 g numbers to the weight, keeping a
    // nutrient nobody measured as "not measured" so it shows as "--" rather than 0
    if (isIngredientMacro(ingredient)) {
      const scaledNutrients = nutrientsForWeightForDisplay(ingredient.nutrients, ingredient.weight_g, 100);
      return FormatNutrients(scaledNutrients, this.userDb);
    }

    // for real ingredients, use cached nutrients from updateNutrientsAsync
    if (ingredientData.cachedNutrients) {
      return FormatNutrients(ingredientData.cachedNutrients, this.userDb);
    }

    return [];
  }

  // Check if ingredient is locked
  isIngredientLocked(ingredient: Ingredient): boolean {
    return isIngredientReal(ingredient) && ingredient.locked === true;
  }

  // Get the serving amount for display (e.g., 1.8 for "1.8 cups")
  getIngredientServingAmount(ingredient: Ingredient): number | undefined {
    if (!isIngredientReal(ingredient) || !ingredient.custom_unit) {
      return undefined;
    }
    const ingredientData = this.ingredientDataMap.get(ingredient);
    if (!ingredientData?.customIngredientUnits) {
      return undefined;
    }
    const unit = ingredientData.customIngredientUnits.find(u => u.id === ingredient.custom_unit);
    if (!unit?.value1) {
      return undefined;
    }
    return ingredient.weight_g / unit.value1;
  }

  // Show popup menu for a specific ingredient
  async showIngredientMenu(event: Event, ingredient: Ingredient) {
    event.stopPropagation();

    const ingredientIndex = this.ingredients.indexOf(ingredient);
    const isRecipe = this.isIngredientSavedRecipe(ingredient);
    const isLocked = isIngredientReal(ingredient) && ingredient.locked;

    const menuItems: PopupMenuButton[] = [];

    // in readonly mode, only show "View Food Details" for real ingredients
    if (this.isReadOnly) {
      if (isIngredientReal(ingredient)) {
        menuItems.push({
          text: LL.ingredientSearch.buttons.viewFoodDetails(),
          handler: () => {
            void this.navCtrl.pushAsync(IEditFoodPage, { id: ingredient.foodId, mode: 'view' });
          },
        });
      }
    } else {
      // if recipe, add "Split Into Foods" first. not for a recipe whose loaded document is known
      // to have no ingredients: it splits into nothing, so the item could never do anything
      // (df-5e2bcf38fbf2). a row whose food could not be read keeps the item, as before.
      if (isRecipe && isIngredientReal(ingredient) && this.ingredientDataMap.get(ingredient)?.hasSplitParts !== false) {
        menuItems.push({
          text: LL.ingredientSearch.buttons.splitIntoFoods(),
          handler: () => {
            void this.handleSplitIngredient(ingredient, ingredientIndex);
          },
        });
      }

      // "Edit Serving Size"
      menuItems.push({
        text: LL.ingredientSearch.buttons.editServingSize(),
        handler: () => {
          void this.openEditIngredientModal(ingredientIndex, EditIngredientMode.Edit);
        },
      });

      // "View Food Details" - only for real ingredients
      if (isIngredientReal(ingredient)) {
        menuItems.push({
          text: LL.ingredientSearch.buttons.viewFoodDetails(),
          handler: () => {
            void this.navCtrl.pushAsync(IEditFoodPage, { id: ingredient.foodId, mode: 'view' });
          },
        });
      }

      // "Lock Food" / "Unlock Food" - only for real ingredients
      if (isIngredientReal(ingredient)) {
        menuItems.push({
          text: isLocked
            ? LL.ingredientSearch.buttons.unlockFood()
            : LL.ingredientSearch.buttons.lockFood(),
          helpIcon: true,
          description: LL.ingredientSearch.tooltips.lockFood(),
          handler: () => {
            ingredient.locked = !ingredient.locked;
            this.updateIngredients.emit(this.ingredients);
            // the toggle saves the meal, so it gets an event too. `locked` is the value it
            // was set TO, not the value it had (#4536).
            this.telemetry.trackEvent('action_ingredient_lock', {
              foodId: foodIdForTelemetry(ingredient),
              locked: ingredient.locked === true,
            });
            this.cd.markForCheck();
          },
        });
      }

      // "Remove Food"
      menuItems.push({
        text: LL.ingredientSearch.buttons.removeFood(),
        handler: async () => {
          // `removeIngredient` takes the food out and records the removal; this tells the
          // host page and asks for a repaint once it has. the recalculation it awaits is slow
          // enough for the user to leave, so the repaint is guarded and the emit deliberately
          // is not -- ingredient-list-destroy-guard.test.ts pins that order and says why.
          await this.removeIngredient(ingredient);
          this.updateIngredients.emit(this.ingredients);
          if (this.destroyGuard.isDestroyed) return;
          this.cd.markForCheck();
        },
      });
    }

    await this.popupMenuProvider.createMenu({
      buttons: menuItems,
      targetFromElement: event.target as HTMLElement,
    });
  }

  // Handle splitting a recipe/complex food into its individual ingredients
  private async handleSplitIngredient(ingredient: IngredientReal, ingredientIndex: number) {
    const food = await this.foodDb.getFoodByIdAsync(ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (!food || !isComplexFood(food)) {
      return;
    }

    try {
      // pass the weight of the entry being split, so the user gets their portion of the recipe
      // rather than the whole recipe (#4427)
      const { ingredients: subIngredients, basedOnFoodId } = await this.foodDb.getSubIngredientsAsync(
        food as ComplexFood,
        { portionWeight_g: ingredient.weight_g },
      );
      // if the split produced nothing, leave the meal exactly as it is. splicing an empty
      // list in would REMOVE the food the user logged and put nothing back, which is how
      // #4427 emptied a whole meal. the service has already reported why it was empty.
      if (subIngredients.length === 0) {
        return;
      }
      const before = deepClone(this.ingredients);
      const markerBefore = this.basedOnFoodId;
      this.ingredients.splice(ingredientIndex, 1, ...subIngredients);
      // this recalculates the totals as its last step, so nothing here starts a second
      // recalculation of the same list -- that is what df-ad69b55547e9 was about.
      void this.loadIngredientsAsync();
      // the meal is "based on" the recipe only when the recipe was its only row (#6188). Set
      // before the list is reported, so the host's save does not judge the new foods against
      // the source the meal had before.
      this.setSourceMarker(markerAfterAdding(before.length === 1, markerBefore, basedOnFoodId));
      this.updateIngredients.emit(this.ingredients);
      // the two reads above are awaited, so the user can have left the screen by now. same
      // reasoning as the card menu's "Remove Food": guard the repaint, not the emits. The toast
      // is guarded too: its Undo needs this screen, and would do nothing once it is gone.
      if (this.destroyGuard.isDestroyed) return;
      this.showSplitToast(this.describeListChange(before, markerBefore, subIngredients, ingredient));
      this.cd.markForCheck();
    } catch (e) {
      // the split throws for real reasons -- chiefly a recipe naming a food that is no longer
      // in the database -- and this used to be a bare console.error, which reaches neither
      // Sentry nor the user. The user tapped a button and nothing happened, and nobody
      // found out. Report it and say so.
      this.logging.trackAbnormalException(e, LL.errors.userFacing.splitIntoFoodsFailed());
    }
  }

  // Handle header menu button click - show combined menu with parent items + reorder
  async onHeaderMenuClick(event: Event) {
    const buttons: PopupMenuButton[] = [
      ...this.headerMenuItems,
    ];

    // add reorder option last (only on mobile when 2+ ingredients)
    if (!this.isReadOnly && this.ingredients.length > 1 && hasClientSmallScreen()) {
      buttons.push({
        text: LL.ingredientSearch.buttons.reorder(),
        handler: () => this.enterReorderMode(),
      });
    }

    await this.popupMenuProvider.createMenu({
      buttons,
      targetFromElement: event.target as HTMLElement,
    });
  }

  // Check if there are any ingredients
  get hasIngredients(): boolean {
    return this.ingredients && this.ingredients.length > 0;
  }

  // Track by function for ngFor - uses stable keys to prevent DOM recreation during reorder
  trackByIngredient(_index: number, ingredient: Ingredient): string {
    let key = this.ingredientKeyMap.get(ingredient);
    if (!key) {
      key = `ingredient-${this.nextIngredientKey++}`;
      this.ingredientKeyMap.set(ingredient, key);
    }
    return key;
  }

  /**
   * Repeat the meal offered up front, in ONE TAP (#4969).
   *
   * Every other route into a recent meal opens the portion screen first and lets the user
   * confirm there. This one does not: the product owner chose the one-tap route with the undo
   * the app already established for logging a single food that way (#4967), so what happens
   * here is deliberately the same shape as `handleEditResult`'s new-ingredient branch —
   * put the foods in the list, then say so and offer to take them back.
   *
   * A meal that turns out to hold nothing we can log is REPORTED and said out loud, rather
   * than leaving a button that visibly does nothing. It should not be reachable: the loader
   * skips meals with no ingredients, so getting here means the stored meal changed underneath
   * the list or was written in a shape this version cannot read.
   */
  protected handleRepeatLastMeal(meal: FoodHeader) {
    const repeated = ingredientsToRepeat(meal.cachedFood as Log_Meal | undefined);
    if (repeated.length === 0) {
      this.logging.trackSilentProblem('RepeatMealHadNoUsableIngredients', { mealId: meal._id });
      this.toastCtrl.showToast({
        message: LL.ingredientSearch.repeatMeal.couldNotRepeatToast(),
        type: ToastType.WarningLight,
        duration: FOOD_ADDED_TOAST_DURATION_MS,
      });
      return;
    }

    const before = deepClone(this.ingredients);
    const markerBefore = this.basedOnFoodId;
    for (const ingredient of repeated) {
      this.ingredients.push(ingredient);
      this.totalWeight += ingredient.weight_g;
      // Counted per food, with the same event every other way of adding one uses: the user
      // added several foods here, and a repeat should read as those foods in the numbers
      // rather than as an event of its own.
      this.telemetry.trackEvent('action_ingredient_add', {
        status: FoodStatus.Recent,
        foodId: foodIdForTelemetry(ingredient),
      });
    }

    // an empty meal takes the past meal's own source marker, as every route that reuses a past
    // meal does (#6188). Set before the list is reported, so the host's save does not judge the
    // new foods against the source the meal had before.
    this.setSourceMarker(markerAfterAdding(before.length === 0, markerBefore, (meal.cachedFood as Log_Meal | undefined)?.basedOnFoodId));
    this.updateTotalWeight.emit(this.totalWeight);
    this.updateIngredients.emit(this.ingredients);
    // the new cards have no display data of their own yet — without this they draw as blanks
    // until something else reloads the list, and on this path nothing else does. It also
    // recalculates the totals as its last step, which is why nothing here starts a second
    // recalculation: one run is all this path needs, and a needless overlapping run is work
    // done twice. An overlap is no longer WRONG, though -- `updateNutrientsAsync` discards a
    // run that a newer one has superseded, so a food can no longer be counted into two runs.
    void this.loadIngredientsAsync();
    this.cd.markForCheck();

    this.showFoodsAddedToast(this.describeListChange(before, markerBefore, repeated));
  }

  /**
   * Describe a change that was just made to the list, for the Undo its toast offers (#6188).
   *
   * Call it straight after the change, with `before` and `markerBefore` taken straight before
   * it: the list as written is read here, and the added foods are found where they now sit.
   * `replaced` is the row a split in place took the place of; leave it out when foods were only
   * added.
   */
  private describeListChange(
    before: Ingredient[],
    markerBefore: string | undefined,
    added: Ingredient[],
    replaced?: Ingredient,
  ): UndoableListChange {
    return {
      before,
      written: deepClone(this.ingredients),
      added,
      addedIndex: this.ingredients.indexOf(added[0]),
      replaced: replaced && deepClone(replaced),
      markerBefore,
    };
  }

  /**
   * Set the meal's source marker to `marker`, telling the host page when it changes (#6188).
   *
   * The local copy is updated at once rather than waiting for the host to bind it back, so a
   * second change made before the next repaint starts from the right marker.
   */
  private setSourceMarker(marker: string | undefined) {
    if (marker === this.basedOnFoodId) return;
    this.basedOnFoodId = marker;
    this.updateBasedOnFoodId.emit(marker);
  }

  /**
   * Say that several foods went into the list in one step, and offer to take them back (#4969,
   * #5808, #6188).
   *
   * One method for every such route — the one-tap Add Again, a past meal confirmed on the
   * portion screen, a newly picked recipe split into its foods — so they cannot word it
   * differently or offer a different Undo. That difference is what #5808 was filed about.
   */
  private showFoodsAddedToast(change: UndoableListChange) {
    const count = change.added.length;
    this.toastCtrl.showToast({
      // Blank whenever the host did not name where the foods are going — the recipe editor,
      // chiefly, which uses this same list. The shorter wording drops the "added to X" half
      // rather than rendering "added to " with nothing after it, the way the single-food
      // toast does.
      message: this.currentMealName
        ? LL.ingredientSearch.repeatMeal.addedToast({ count, meal: this.currentMealName })
        : LL.ingredientSearch.repeatMeal.addedToastNoDestination({ count }),
      actionButtonText: LL.common.buttons.undo(),
      onAction: () => this.undoListChange(change),
      ...FOOD_ADDED_TOAST_PRESENTATION,
    });
  }

  /**
   * Say that a recipe row already in the meal was split into its foods, and offer to put the
   * row back (#6188). The product owner chose a toast for every split, not only for a newly
   * picked recipe, so a split made by a stray tap can be taken back in one step.
   */
  private showSplitToast(change: UndoableListChange) {
    this.toastCtrl.showToast({
      message: LL.ingredientSearch.splitToast({ count: change.added.length }),
      actionButtonText: LL.common.buttons.undo(),
      onAction: () => this.undoListChange(change),
      ...FOOD_ADDED_TOAST_PRESENTATION,
    });
  }

  /**
   * Take back a change one of the toasts above announced, and put the meal's source marker back
   * to what it was before it (#6188).
   *
   * What the list becomes is `undoListChange`'s job, and its doc comment carries the rule: the
   * list exactly as it was when nothing has changed since, otherwise only the added foods out.
   *
   * An Undo that can take out only SOME of the added foods is still carried out: the user
   * pressed Undo and every food that can still be taken out should be. The shortfall is
   * reported, because a food that vanished between adding it and undoing it is something a
   * future debugger will want to know about. An Undo that can do nothing safely says so.
   */
  private undoListChange(change: UndoableListChange) {
    if (this.destroyGuard.isDestroyed) return;

    const isSplitInPlace = change.replaced !== undefined;
    const undo = undoListChange(change, this.ingredients);
    if (undo.outcome === ListUndoOutcome.NotPossible) {
      // named for what was undone, not for one route: a past meal and a newly picked recipe
      // split both arrive here as foods that were added
      this.logging.trackSilentProblem(isSplitInPlace ? 'SplitUndoPartsGone' : 'AddedFoodsUndoIngredientsGone',
        { count: change.added.length });
      this.toastCtrl.showToast({
        message: isSplitInPlace
          ? LL.ingredientSearch.splitUndoFailedToast()
          : LL.ingredientSearch.repeatMeal.undoFailedToast(),
        type: ToastType.WarningLight,
        duration: FOOD_ADDED_TOAST_DURATION_MS,
      });
      return;
    }
    if (undo.outcome === ListUndoOutcome.Partly) {
      this.logging.trackSilentProblem('AddedFoodsUndoPartial', {
        removed: undo.removed,
        expected: undo.expected,
      });
    }

    // each food out counts as a removal, like every other way a food leaves a meal. The
    // `source` tells a take-back apart from a deliberate delete (#4536).
    for (const ingredient of undo.takenOut) {
      this.telemetry.trackEvent('action_ingredient_remove', {
        foodId: foodIdForTelemetry(ingredient),
        source: 'added-undo',
      });
    }

    this.ingredients = undo.ingredients;
    // the marker goes back BEFORE the list is reported, so the host's save does not judge the
    // restored foods against the source the change brought in
    this.setSourceMarker(change.markerBefore);
    this.updateIngredients.emit(this.ingredients);
    // a restored list holds fresh copies with no display data yet. This reload draws them and
    // recalculates the totals and the weight as its last step.
    void this.loadIngredientsAsync();
    this.cd.markForCheck();

    // Counted once, because the user pressed Undo once. Same event as the single-food undo:
    // the two take back different amounts, but they are one thing to the person doing it.
    this.telemetry.trackEvent('food_added_undo', {});
  }

  // Handle picking a recent meal in the empty state (the child component stops the click itself)
  async handleRecentMealClick(meal: FoodHeader) {
    if (!meal._id) return;
    try {
      await this.showLogMealConfirmationAsync(meal._id);
    } catch (e) {
      this.logging.trackAbnormalException(e);
    }
  }

  /**
   * "View All" on the recent meals: opens the Foods page filtered to past meals.
   *
   * Unlike Add Foods above, this route does NOT keep the list open under the portion screen
   * (#5808). The list closes when a meal is tapped, so Log lands on the meal with the foods in
   * it, the same place Add Again leaves the user. Somebody reusing a whole past meal is almost
   * always done once it is in. The price is that Back on the portion screen lands on the meal
   * too. See `design-docs/nutrition-logging-routes.md`, "Where Log lands afterwards".
   */
  handleViewAllRecentMeals() {
    void this.navCtrl.pushAsync(IFoodsPage, {
      mode: 'select',
      filter: 'meals',
      returnCallback: 'handleFoodPageResult',
      local: {
        returnContext: { source: 'viewAllRecentMeals' },
      },
    });
  }

  // Create a placeholder ingredient that represents a log meal for preview in EditIngredientPage.
  // `name` deliberately keeps the STORED English name (e.g. "Meal 3") rather than a
  // translated one (#4317): this object is an IngredientReal that can flow into a saved
  // meal, and a translated name written into the user's data would freeze their current
  // language into it. Translation belongs at the render site, not here.
  private createPlaceholderFromLogMeal(logMeal: Log_Meal): IngredientReal {
    const totalCalories = logMeal.nutrients?.ENERC_KCAL || 0;
    return {
      type: IngredientType.Real,
      foodId: logMeal._id,
      name: logMeal.name,
      weight_g: logMeal.ingredients?.reduce((sum, ing) => sum + (ing.weight_g || 0), 0) || 0,
      kcal: totalCalories,
      status: FoodStatus.Recent,
    };
  }

  // Handle "Add Foods" button in empty state
  handleAddFoods() {
    this.handleFindFood();
  }

  // Handle "Log Macros Manually" button in empty state
  handleLogMacrosManually() {
    void this.addCustomFoodAsync();
    this.quickMacrosHandler.emit();
  }

  // Handle result from FoodsPage - called by parent page's handleFoodPageResult
  async handleFoodPageResult(_context: { source: string }, result: FoodsPageResult) {
    if (!result) return;

    switch (result.action) {
      case 'select':
        // if this is a recent meal, copy its ingredients
        if (isLogMealById(result.foodId)) {
          await this.addIngredientsFromLogMealAsync(result.foodId);
        } else {
          await this.addIngredientToListAsync(result.foodId);
        }
        break;

      case 'addMacros':
        void this.addCustomFoodAsync();
        break;
    }

    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
  }
}
